import React, { useState, useEffect } from 'react';
import { Typography, Alert } from 'antd';

const StorageTankDisplay = ({ sensor, asset, compact = false }) => {

  const { Text } = Typography;
  const [animatedFill, setAnimatedFill] = useState(0);

  // Get the most recent distance reading
  const getLastKnownValue = (sensor, sensorType) => {
    if (!sensor.timelines || !sensor.timelines.length) {
      return null;
    }
    for (let i = sensor.timelines.length - 1; i >= 0; i--) {
      const timeline = sensor.timelines[i];
      if (timeline[sensorType]) {
        return {
          value: timeline[sensorType].value,
          unit: timeline[sensorType].unit || '',
          time: timeline[sensorType].time
        };
      }
    }
    return null;
  };

  // Convert distance to millimeters based on unit
  const convertToMillimeters = (value, unit) => {
    const normalizedUnit = (unit || '').toLowerCase().trim();
    
    switch (normalizedUnit) {
      case 'mm':
      case 'millimeter':
      case 'millimeters':
        return value;
      case 'cm':
      case 'centimeter':
      case 'centimeters':
        return value * 10;
      case 'm':
      case 'meter':
      case 'meters':
        return value * 1000;
      default:
        // If no unit specified, assume mm
        return value;
    }
  };

  // Calculate the current level based on sensor distance or height data
  const calculateTankLevel = () => {
    if (!sensor.timelines || !sensor.timelines.length) {
      return null;
    }

    // Try to get distance reading first, then height reading
    let lastReading = getLastKnownValue(sensor, 'distance');
    let sensorType = 'distance';
    
    if (!lastReading || lastReading.value == null) {
      lastReading = getLastKnownValue(sensor, 'height');
      sensorType = 'height';
    }
    
    if (!lastReading || lastReading.value == null) {
      return null;
    }

    // Convert to millimeters based on the unit
    const distanceInMm = convertToMillimeters(lastReading.value, lastReading.unit);

    const heightAt100 = parseFloat(asset.height_at_100);
    const heightAt0 = parseFloat(asset.height_at_0);
    const litresAt100 = parseFloat(asset.litres_at_100);
    const litresAt0 = parseFloat(asset.litres_at_0);
    const measurementDirection = asset.measurement_direction || 'top_to_bottom';

    // Validate configuration
    if (isNaN(heightAt100) || isNaN(heightAt0) || isNaN(litresAt100) || isNaN(litresAt0)) {
      return null;
    }

    let percentage;
    let heightFromEmpty;
    let isOverflow = false;
    let isUnderflow = false;

    if (measurementDirection === 'bottom_to_top') {
      // Bottom to top: height increases as tank fills
      const heightRange = heightAt100 - heightAt0;
      heightFromEmpty = distanceInMm - heightAt0;
      percentage = (heightFromEmpty / heightRange) * 100;
      
      // Check for overflow/underflow
      if (percentage > 100) {
        isOverflow = true;
      } else if (percentage < 0) {
        isUnderflow = true;
      }
    } else {
      // Top to bottom (default): height decreases as tank fills
      const heightRange = heightAt0 - heightAt100;
      heightFromEmpty = heightAt0 - distanceInMm;
      percentage = (heightFromEmpty / heightRange) * 100;
      
      // Check for overflow/underflow
      if (percentage > 100) {
        isOverflow = true;
      } else if (percentage < 0) {
        isUnderflow = true;
      }
    }

    // Clamp percentage for display (but keep actual value for calculations)
    const actualPercentage = percentage;
    const displayPercentage = Math.max(0, Math.min(100, percentage));

    // Calculate current litres using linear interpolation (can exceed max)
    const litresRange = litresAt100 - litresAt0;
    const currentLitres = litresAt0 + (actualPercentage / 100) * litresRange;

    return {
      percentage: displayPercentage.toFixed(1),
      actualPercentage: actualPercentage.toFixed(1),
      currentLitres: Math.round(currentLitres),
      distanceReading: distanceInMm.toFixed(0),
      distanceUnit: 'mm',
      originalReading: lastReading.value.toFixed(2),
      originalUnit: lastReading.unit || 'mm',
      maxLitres: Math.round(litresAt100),
      lastUpdate: lastReading.time,
      measurementDirection,
      isOverflow,
      isUnderflow,
      sensorType
    };
  };

  const tankData = calculateTankLevel();
  const fillPercentage = tankData && !tankData.error ? parseFloat(tankData.percentage) : 0;

  useEffect(() => {
    if (tankData && !tankData.error) {
      const timeout = setTimeout(() => setAnimatedFill(fillPercentage), 150);
      return () => clearTimeout(timeout);
    }
  }, [fillPercentage, tankData]);

  if (!tankData) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '20px', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '6px', 
        marginTop: '8px' 
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          No tank level data available
        </Text>
      </div>
    );
  }

  const fillColor = tankData.isOverflow || tankData.isUnderflow ? "#ff9800" : "#0E4128";

  // Compact mode for TV display
  if (compact) {
    // Dynamic font size based on number of digits
    const litresString = tankData.currentLitres.toLocaleString();
    const digitCount = litresString.replace(/,/g, '').length;
    const fontSize = digitCount > 3 ? '20px' : '24px';
    const unitSize = digitCount > 3 ? '12px' : '14px';

    return (
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '12px'
      }}>
        <div style={{ position: 'relative', width: '70px', height: '90px' }}>
          <svg width="70" height="90" viewBox="0 0 70 90" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`tankGradient-${sensor.id}-compact`} x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" style={{ stopColor: fillColor, stopOpacity: 0.95 }} />
                <stop offset="100%" style={{ stopColor: fillColor, stopOpacity: 0.7 }} />
              </linearGradient>

              <linearGradient id={`tankBody-${sensor.id}-compact`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f0f0f0" />
                <stop offset="50%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#e0e0e0" />
              </linearGradient>

              <filter id={`shadow-${sensor.id}-compact`} x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#999" floodOpacity="0.4" />
              </filter>
            </defs>

            {/* Tank body */}
            <rect
              x="8"
              y="20"
              width="54"
              height="50"
              rx="4"
              fill="white"
              stroke="#555"
              strokeWidth="1.5"
              filter={`url(#shadow-${sensor.id}-compact)`}
            />

            {/* Tank fill with animation */}
            <rect
              x="9"
              y={21 + (47 * (100 - animatedFill) / 100)}
              width="52"
              height={47 * animatedFill / 100}
              rx="0"
              fill={`url(#tankGradient-${sensor.id}-compact)`}
              style={{
                transition: 'all 1.8s ease-in-out',
              }}
            />

            {/* Top cap */}
            <ellipse
              cx="35"
              cy="22"
              rx="27"
              ry="6"
              fill="#dcdcdc"
              stroke="#555"
              strokeWidth="1.5"
            />

            {/* Bottom cap */}
            <ellipse
              cx="35"
              cy="68"
              rx="27"
              ry="6"
              fill={fillColor}
              stroke="#0E4128"
              strokeWidth="1.5"
            />

            {/* Subtle top shine */}
            <ellipse
              cx="35"
              cy="22"
              rx="25"
              ry="5"
              fill="white"
              opacity="0.4"
            />
          </svg>

          {/* Percentage overlay */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontWeight: 'bold',
            fontSize: '14px',
            color: 'white',
            textShadow: '1px 1px 2px rgba(0,0,0,0.9), -1px -1px 2px rgba(0,0,0,0.9)'
          }}>
            {tankData.percentage}%
          </div>
        </div>

        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: fontSize, fontWeight: 'bold', color: fillColor, lineHeight: 1 }}>
            {litresString}
            <span style={{ fontSize: unitSize, marginLeft: '4px' }}>L</span>
          </div>
          {(tankData.isOverflow || tankData.isUnderflow) && (
            <Text type="warning" style={{ fontSize: '9px', display: 'block', color: '#ff9800' }}>
              ⚠ Config Issue
            </Text>
          )}
        </div>
      </div>
    );
  }

  // Regular mode
  return (
    <div style={{ 
      marginTop: '12px',
      marginBottom: '12px',
      padding: '0'
    }}>
      
      {(tankData.isOverflow || tankData.isUnderflow) && (
        <Alert
          message={tankData.isOverflow ? "Over maximum - check configuration or calibrate sensor" : "Under minimum - check configuration or calibrate sensor"}
          type="warning"
          showIcon
          style={{ marginBottom: '12px', fontSize: '12px' }}
          banner
        />
      )}
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '16px',
        backgroundColor: '#f5f7f5',
        padding: '16px',
        borderRadius: '8px',
        border: `1px solid ${tankData.isOverflow || tankData.isUnderflow ? fillColor : '#e8e8e8'}`
      }}>
        {/* Tank SVG - Regular version */}
        <div style={{ position: 'relative', width: '100px', height: '100px', flexShrink: 0 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`tankGradient-${sensor.id}`} x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" style={{ stopColor: fillColor, stopOpacity: 0.95 }} />
                <stop offset="100%" style={{ stopColor: fillColor, stopOpacity: 0.7 }} />
              </linearGradient>

              {/* Tank metallic shine */}
              <linearGradient id={`tankBody-${sensor.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f0f0f0" />
                <stop offset="50%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#e0e0e0" />
              </linearGradient>

              {/* Subtle shadow */}
              <filter id={`shadow-${sensor.id}`} x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#999" floodOpacity="0.4" />
              </filter>
            </defs>

            {/* Tank body */}
            <rect
              x="10"
              y="25"
              width="80"
              height="50"
              rx="6"
              fill={`url(#tankBody-${sensor.id})`}
              stroke="#555"
              strokeWidth="1.5"
              filter={`url(#shadow-${sensor.id})`}
            />

            {/* Tank fill with animation */}
            <rect
              x="11"
              y={27 + (46 * (100 - animatedFill) / 100)}
              width="78"
              height={46 * animatedFill / 100}
              rx="0"
              fill={`url(#tankGradient-${sensor.id})`}
              style={{
                transition: 'all 1.8s ease-in-out',
              }}
            />

            {/* Top cap */}
            <ellipse
              cx="50"
              cy="28"
              rx="40"
              ry="8"
              fill="#dcdcdc"
              stroke="#555"
              strokeWidth="1.5"
            />

            {/* Bottom cap */}
            <ellipse
              cx="50"
              cy="72"
              rx="40"
              ry="8"
              fill={fillColor}
              stroke="#0E4128"
              strokeWidth="1.5"
            />

            {/* Subtle top shine */}
            <ellipse
              cx="50"
              cy="28"
              rx="38"
              ry="6"
              fill="white"
              opacity="0.4"
            />
          </svg>

          {/* Percentage overlay */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontWeight: 'bold',
            fontSize: '16px',
            color: 'white',
            textShadow: '1px 1px 2px rgba(0,0,0,0.9), -1px -1px 2px rgba(0,0,0,0.9)'
          }}>
            {tankData.percentage}%
          </div>
        </div>

        {/* Tank information */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '10px',
          flex: 1,
          minWidth: 0
        }}>
          {/* Volume - Primary info */}
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', flexWrap: 'wrap' }}>
              <Text strong style={{ fontSize: '22px', color: fillColor, lineHeight: 1 }}>
                {tankData.currentLitres.toLocaleString()}
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                / {tankData.maxLitres.toLocaleString()} L
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '2px' }}>
              Current Volume
            </Text>
          </div>

          {/* Distance reading - Secondary info */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            paddingTop: '8px',
            borderTop: '1px solid #e0e0e0',
            gap: '8px'
          }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                Sensor Reading
              </Text>
              <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                {tankData.distanceReading} mm
              </Text>
            </div>
            
            {tankData.lastUpdate && (
              <div style={{ flex: 1, textAlign: 'right' }}>
                <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                  Last Updated
                </Text>
                <Text style={{ fontSize: '11px' }}>
                  {tankData.lastUpdate}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageTankDisplay;